import type { Answer, AnswerValue, Survey } from "@shared-domain/survey/contract";
import { effectiveVisibleIds } from "@shared-domain/survey/logic";
import { todayJst } from "./theme";

/** 編集中の回答は questionId→値のマップで持ち、送信時に Answer[] に変換する。 */
export type AnswerMap = Record<string, AnswerValue>;

export const emptyAnswerMap = (survey: Survey): AnswerMap => {
  const m: AnswerMap = {};
  for (const q of survey.questions) {
    if (q.type === "checkbox") m[q.questionId] = [];
    else if (q.type === "date") m[q.questionId] = todayJst();
  }
  return m;
};

const isEmpty = (v: AnswerValue | undefined): boolean =>
  v === undefined ||
  v === null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

export const toAnswers = (m: AnswerMap): Answer[] =>
  Object.entries(m)
    .filter(([, v]) => !isEmpty(v))
    .map(([questionId, value]) => ({ questionId, value }));

export const fromAnswers = (answers: Answer[]): AnswerMap =>
  Object.fromEntries(answers.map((a) => [a.questionId, a.value]));

export const answerMapToVisMap = (m: AnswerMap): Map<string, AnswerValue> =>
  new Map(Object.entries(m));

/**
 * 表示条件の親が切り替わって非表示になった設問の値をクリアする (PRODUCT_DEV-574)。
 * 非表示設問は emptyAnswerMap と同じ既定値に戻す (checkbox→[]、date→todayJst()、他→キー削除)。
 * 変化がなければ同一参照を返す (不要な再レンダー回避)。
 */
export const pruneHiddenAnswers = (survey: Survey, m: AnswerMap): AnswerMap => {
  const visible = effectiveVisibleIds(survey.questions, answerMapToVisMap(m));
  let next: AnswerMap | null = null;
  for (const q of survey.questions) {
    if (visible.has(q.questionId)) continue;
    const cur = m[q.questionId];
    const def: AnswerValue | undefined =
      q.type === "checkbox" ? [] : q.type === "date" ? todayJst() : undefined;
    const already =
      def === undefined
        ? cur === undefined
        : Array.isArray(def)
          ? Array.isArray(cur) && cur.length === 0
          : cur === def;
    if (already) continue;
    next ??= { ...m };
    if (def === undefined) delete next[q.questionId];
    else next[q.questionId] = def;
  }
  return next ?? m;
};
